import { motion, AnimatePresence } from "framer-motion";
import * as cx from "classnames/bind";
import {
  Button,
  ButtonLink,
  Cocktail,
  Container,
  CreateListModal,
  Icon,
  Popup,
  Section,
  Loader,
} from "components";
import { useDebounce, useRecipeLists, useSession } from "hooks";
import { HomeLayout } from "layouts";
import sortBy from "lodash.sortby";
import isEqual from "lodash.isequal";
import groupBy from "lodash.groupby";
import Image from "next/legacy/image";
import Link from "next/link";
import Head from "next/head";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { runQuery } from "queries";
import { useEffect, useState, useRef } from "react";
import {
  useHits,
  useSearchBox,
  Configure,
  InstantSearch,
  useInfiniteHits,
  useInstantSearch,
  useRefinementList,
  useCurrentRefinements,
  useClearRefinements,
} from "react-instantsearch";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import s from "styles/Gallery.module.scss";
import urlSlug from "url-slug";
import { imageUrl, fetcher, uniqObjArray, uniqObjArrayOld } from "utils";
import is from "styles/Input.module.scss";
import store from "store2";
import AsyncSelect from "react-select/async";
import Cookies from "js-cookie";

import { createInstantSearchRouterNext } from "react-instantsearch-router-nextjs";
import singletonRouter from "next/router";

import { createInfiniteHitsSessionStorageCache } from "instantsearch.js/es/lib/infiniteHitsCache";
const sessionStorageCache = createInfiniteHitsSessionStorageCache();

import { gql } from "graphql-request";

import algoliasearch from "algoliasearch";

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY,
);

const limit = 24;
const indexName = "cocktailclub-cocktails-en";

const ResetButton = (props) => {
  const { t } = useTranslation();
  const { refine, canRefine } = useClearRefinements(props);
  return (
    canRefine && (
      <Button
        disabled={!canRefine}
        className="ButtonLink warning p0"
        onClick={() => {
          refine();
        }}
      >
        {t("Reset filter")}
      </Button>
    )
  );
};

const SelectedFilters = ({ ...props }) => {
  const { items, canRefine, refine } = useCurrentRefinements(props);
  return (
    <div className={cx(s.FilterResults, s.inPopup)}>
      {items.map(({ refinements, attribute = "" }) =>
        refinements.map((item) => {
          const dbItem = (props[attribute.split(".")[0]] || []).find(
            (dbItem) => dbItem.slug == item.label,
          );
          return dbItem ? (
            <div onClick={() => refine(item)} key={item.value}>
              {dbItem.name}
              <span></span>
            </div>
          ) : (
            <></>
          );
        }),
      )}
    </div>
  );
};

const Filter = ({ attribute, label, operator = "or", ...props }) => {
  const { t } = useTranslation();
  const {
    items,
    hasExhaustiveItems,
    createURL,
    refine,
    sendEvent,
    searchForItems,
    isFromSearch,
    canRefine,
    canToggleShowMore,
    isShowingMore,
    toggleShowMore,
  } = useRefinementList({
    attribute: `${attribute}.slug`,
    limit: 1000,
    operator,
  });
  var dbItems = props[attribute] || [];
  const numRefined = items.filter((r) => r.isRefined).length;
  return (
    <div className={cx(s.FilterItem)}>
      <div className={cx(s.FilterItemHead)}>
        <h4 className="Heading__sub">
          {label}
          <span>{numRefined}</span>
        </h4>
        <span>
          {t("Select")} {label}
        </span>
        <img alt="" src="/img/icons/chevron-down-light.svg" />
      </div>
      <div className={cx(s.FilterItemBody)}>
        {items.map((item, index) => {
          const dbItem = dbItems.find((dbItem) => dbItem.slug == item.label);
          if (!dbItem) {
            return <></>;
          }
          const parentItem = dbItem.parent_id
            ? items.find(
                (i) =>
                  i.label ==
                  dbItems.find((parent) => parent.id == dbItem.parent_id)?.slug,
              )
            : null;
          if (attribute == "spirits") {
            if (props.subCategory) {
              if (!dbItem.parent_id || !parentItem.isRefined) {
                return <></>;
              }
            } else if (!!dbItem.parent_id) {
              return <></>;
            }
          }
          return (
            <div
              className="checkContainer"
              key={item.value}
              onClick={() => refine(item.value)}
            >
              <input id={item.label} type="checkbox" checked={item.isRefined} />
              &nbsp;
              <span>{dbItem.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Filters = ({ open, handleTrigger }) => {
  const { t } = useTranslation();

  const [loading, setIsLoading] = useState(false);
  const [state, setState] = useState({ loaded: false });

  useEffect(() => {
    async function fetchData() {
      const createPromise = (name) =>
        new Promise(async (resolve) => {
          const query = gql`
            query {
              ${name} {
                name
                slug
              }
            }
          `;
          const data = await fetcher(query);
          resolve(data[name]);
        });
      const promises = {
        spirits: new Promise(async (resolve) => {
          const query = gql`
            query {
              spirits: cocktails(
                where: {
                  recipes: {
                    timeline: {
                      steps: {
                        ingredients: { ingredient: { type: { _eq: "spirit" } } }
                      }
                    }
                  }
                  hidden: { _eq: false }
                }
              ) {
                recipes {
                  timeline {
                    steps {
                      ingredients(
                        where: { ingredient: { type: { _eq: "spirit" } } }
                      ) {
                        ingredient {
                          id
                          name
                          slug
                          parent_id
                        }
                      }
                    }
                  }
                }
              }
            }
          `;
          const data = await fetcher(query);
          const unsorted = uniqObjArrayOld(
            data.spirits
              .flatMap((c) =>
                c.recipes[0].timeline.steps.flatMap((s) => s.ingredients),
              )
              .map((i) => i.ingredient),
          );
          const spirits = sortBy(
            unsorted.filter((s) => !s.parent_id),
            (s) => s.name,
          )
            .map((s) =>
              [
                s,
                sortBy(
                  unsorted.filter((o) => o.parent_id == s.id),
                  (s) => s.name,
                ),
              ].flat(),
            )
            .flat();
          resolve(spirits);
        }),
        difficulty: createPromise("difficulties"),
        flavors: createPromise("flavors"),
        occasions: createPromise("occasions"),
        glass: createPromise("glasses"),
      };
      const responses = await Promise.all(Object.values(promises));
      setState({
        loaded: true,
        ...Object.fromEntries(
          Object.keys(promises).map((key, index) => [key, responses[index]]),
        ),
      });
    }
    fetchData();
  }, []);
  return (
    <div className={cx(s.Filter, open ? s.open : "")}>
      <div className={cx(s.FilterOverlay)} onClick={handleTrigger}></div>
      <div className={cx(s.FilterContent)}>
        <div className={cx(s.FilterHead)}>
          <ResetButton />
          <Button className="Hollow" onClick={handleTrigger}>
            {t("Close filter")}
          </Button>
        </div>
        <form>
          <SelectedFilters {...state} />
          <Filter
            attribute="spirits"
            label={t("Spirit")}
            {...state}
            operator="and"
          />
          {/*
          <Filter
            attribute="spirits"
            label={t("Sub categories")}
            {...state}
            operator="or"
            subCategory={true}
          />
          */}
          {/*
          <Filter attribute="flavors" label={t("Flavors")} {...state} />
          */}
          <Filter attribute="occasions" label={t("Occasion")} {...state} />
          <Filter attribute="difficulty" label={t("Difficulty")} {...state} />
          <Filter attribute="glass" label={t("Glass")} {...state} />
          <div className={cx("inputContainer", s.submitButton)}>
            <span
              className={cx("Button Blue")}
              svg="chevron-left"
              size="far"
              onClick={handleTrigger}
            >
              <Icon svg="chevron-left" size="far" />
            </span>
            <div className="Button Primary Full Rad0" onClick={handleTrigger}>
              {t("Show recipes")}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const Results = ({ ...props }) => {
  const { hits, showMore, isLastPage, results } = useInfiniteHits({
    ...props,
    cache: sessionStorageCache,
  });
  const [hasScrolled, setHasScrolled] = useState(false);
  const { t } = useTranslation();
  const router = useRouter();

  useEffect(() => {
    if (router.isReady && !results.__isArtificial && !hasScrolled) {
      //console.info("algolia loaded", hits.length);
      const key = urlSlug(`scroll-${router.asPath}`);
      document.documentElement.scrollTop = parseInt(store.remove(key, 0));
      setHasScrolled(true);
    }
  }, [results.__isArtificial, hits, router.isReady, hasScrolled]);

  return (
    <div className={cx(s.GalleryContainer)}>
      <AnimatePresence>
        <motion.div layout className={cx("GalleryFeed row", s.GalleryFeed)}>
          {(hits || []).map((cocktail) => (
            <Cocktail
              key={cocktail.id}
              cocktail={cocktail}
              setShowCreateListModal={props.setShowCreateListModal}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
            />
          ))}
        </motion.div>
      </AnimatePresence>
      <Button
        className="Primary block mt24 m__auto"
        onClick={showMore}
        disabled={isLastPage}
      >
        {t("Load more")}
      </Button>
    </div>
  );
};

let timerId = undefined;
const queryHook = (query, search) => {
  if (timerId) {
    clearTimeout(timerId);
  }
  timerId = setTimeout(() => search(query), 400);
};

const SearchBox = ({ cocktailsSearchRef }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { refine } = useSearchBox({
    queryHook,
  });
  useEffect(() => {
    if (router.query.s) {
      refine(router.query.s);
    }
  }, []);
  useSearchBox({
    search: router.query.s || "",
  });
  return (
    <>
      <input
        ref={cocktailsSearchRef}
        type="text"
        defaultValue={router.query.s}
        placeholder="Search cocktails..."
        onKeyUp={(e) => {
          refine(e.target.value);
        }}
      />
      <button>
        <Icon size="far" svg="search" />
      </button>
    </>
  );
};

const Cocktails = ({ cocktailsSearchRef, category, ...props }) => {
  const { t } = useTranslation();

  const router = useRouter();

  const [open, setOpen] = useState(false);
  const [algoliaLoaded, setAlgoliaLoaded] = useState(false);
  const [showCreateListModal, setShowCreateListModal] = useState(false);

  const { status, results } = useInstantSearch();
  const handleTrigger = () => {
    setOpen(!open);
  };

  const loading = status == "stalled";
  const country = Cookies.get("__country");
  let filters = "hidden:false AND pro:false AND organization_id:false";
  filters = `${filters} AND ${
    country
      ? `NOT excluded_countries:${country} AND (exclusive_countries:${country} OR is_exclusive:false)`
      : "is_exclusive:false"
  }`;

  const facetFilters = category?.slug
    ? [`categories.slug:${category.slug}`]
    : [];
  return (
    <>
      <Configure
        hitsPerPage={limit}
        query={router.query.s}
        filters={filters}
        facetFilters={facetFilters}
      />
      <Section className="p0">
        <div className={cx("GalleryFeedHeader", s.GalleryFeedHeader)}>
          <Container className="center">
            {category ? (
              <h1 className={cx("Heading__main White Light")}>
                {category.name}&nbsp;{t("collection")}
              </h1>
            ) : (
              <>
                <h1 className={cx("Heading__main White Light", s.feedTitle)}>
                  {t("Recipe tutorials")}
                </h1>
                <div className={cx("searchHolder", s.searchHolder)}>
                  <div>
                    <SearchBox cocktailsSearchRef={cocktailsSearchRef} />
                  </div>
                </div>
                <br />
              </>
            )}
          </Container>
        </div>
      </Section>
      <Section>
        <Container className={cx(s.GalleryContainer)}>
          <div className={cx(s.GalleryHeader, s.GalleryHeaderActions)}>
            <Button
              svg="sliders-h"
              size="far"
              leftIcon
              className={cx("Primary Hollow")}
              onClick={handleTrigger}
            >
              {open ? "Hide filters" : "Edit filters"}
            </Button>

            <div className={cx(s.FilterResults)}>
              <ResetButton />
            </div>
            {/*
            <SortSelect setSortOptions={setSortOptions} options={sortOptions} />
            */}
          </div>
          <Results setShowCreateListModal={setShowCreateListModal} />
          <Filters open={open} handleTrigger={handleTrigger} />
        </Container>
      </Section>
      <CreateListModal
        onClose={() => setShowCreateListModal(false)}
        show={showCreateListModal}
      />
    </>
  );
};

Cocktails.getLayout = (page) => {
  const router = useRouter();
  return (
    <>
      <Head>
        <title key="title">Recipe tutorials - Cocktail Club</title>
        <meta
          key="ogUrl"
          property="og:url"
          content="https://cocktailclub.com/cocktails"
        />
        <link
          key="canonical"
          rel="canonical"
          href="https://cocktailclub.com/cocktails"
        />
        <meta
          key="ogTitle"
          property="og:title"
          content="Recipe tutorials - Cocktail Club"
        />
      </Head>
      <InstantSearch
        searchClient={searchClient}
        indexName={indexName}
        insights={true}
        routing={{
          router: createInstantSearchRouterNext({
            singletonRouter,
            routerOptions: {
              createURL: ({ qsModule, location, routeState }) => {
                const { origin, pathname, hash } = location;
                const indexState = routeState[indexName] || {};
                if (
                  Object.keys(indexState).length < 1 ||
                  indexState.query === undefined
                ) {
                  return `${origin}${pathname}${hash}`;
                }
                let state = {
                  ...(indexState.query ? { s: indexState.query } : {}),
                  //...(indexState.page ? { page: indexState.page } : {}),
                };

                Object.entries(indexState.refinementList || {}).forEach(
                  ([key, value]) => {
                    state[key.split(".")[0]] = value.join(",");
                  },
                );
                const queryString = qsModule.stringify(state, {
                  encode: false,
                });
                return `${origin}${pathname}?${queryString}${hash}`;
              },
              parseURL: ({ qsModule, location }) => {
                const state = qsModule.parse(location.search.slice(1));
                let refinementList = {};
                [
                  "spirits",
                  "occasions",
                  "glass",
                  "flavors",
                  "difficulty",
                ].forEach((facet) => {
                  const val = state[facet];
                  if (val) {
                    refinementList[`${facet}.slug`] = val.split(",");
                  }
                });
                return {
                  [indexName]: { ...state, query: state.s, refinementList },
                };
              },
              push: (url) => {
                router.push(url, null, { scroll: false });
              },
            },
          }),
        }}
      >
        <HomeLayout style={{ paddingTop: "0px" }} progressbar={false}>
          {page}
        </HomeLayout>
      </InstantSearch>
    </>
  );
};

export async function getStaticProps({ locale, params }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
      //...(await runQuery("cocktails/multiple", params)).props,
      //...(await runQuery("filters/all", params)).props,
    },
    revalidate: 60,
  };
}

export default Cocktails;
